
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";

type Worker = {
  id: string;
  name: string;
};

type Worksite = {
  id: string;
  name: string;
  address?: string;
  status: string;
};

export const useAddAssignment = () => {
  const navigate = useNavigate();
  
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [worksites, setWorksites] = useState<Worksite[]>([]);
  const [selectedWorker, setSelectedWorker] = useState<string>("");
  const [selectedWorksite, setSelectedWorksite] = useState<string>("");
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  // Load workers and worksites on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        console.log("🔍 Chargement des ouvriers et chantiers...");
        
        // Load workers
        const { data: workersData, error: workersError } = await supabase
          .from('profiles')
          .select('id, name')
          .eq('role', 'ouvrier')
          .eq('active', true);

        if (workersError) {
          console.error('❌ Erreur lors du chargement des ouvriers:', workersError);
          toast.error("Erreur lors du chargement des ouvriers");
        } else {
          console.log(`✅ ${workersData?.length || 0} ouvriers chargés`);
          setWorkers(workersData || []);
        }

        // Load ALL worksites (not just active ones initially)
        const { data: worksitesData, error: worksitesError } = await supabase
          .from('worksites')
          .select('id, name, address, status')
          .order('name');

        if (worksitesError) {
          console.error('❌ Erreur lors du chargement des chantiers:', worksitesError);
          toast.error("Erreur lors du chargement des chantiers");
        } else {
          // Filter only active worksites
          const activeWorksites = (worksitesData || []).filter(ws => ws.status === 'active');
          console.log(`✅ ${activeWorksites.length} chantiers actifs sur ${worksitesData?.length || 0} total`);
          setWorksites(activeWorksites);
        }
      } catch (error) {
        console.error('❌ Erreur lors du chargement des données:', error);
        toast.error("Erreur lors du chargement des données");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);
  
  const handleSubmit = async () => {
    // Validation
    if (!selectedWorker) {
      toast.error("Veuillez sélectionner un ouvrier");
      return;
    }

    if (!selectedWorksite) {
      toast.error("Veuillez sélectionner un chantier");
      return;
    }

    if (!startDate) {
      toast.error("Veuillez sélectionner une date de début");
      return;
    }

    if (!endDate) {
      toast.error("Veuillez sélectionner une date de fin");
      return;
    }

    if (startDate > endDate) {
      toast.error("La date de fin doit être après la date de début");
      return;
    }

    try {
      console.log("🔍 Création de l'assignation...");
      
      // Insert assignment into database
      const { error } = await supabase
        .from('assignments')
        .insert({
          user_id: selectedWorker,
          worksite_id: selectedWorksite,
          start_date: format(startDate, 'yyyy-MM-dd'),
          end_date: format(endDate, 'yyyy-MM-dd')
        });

      if (error) {
        console.error('❌ Erreur lors de la création de l\'assignation:', error);
        toast.error("Erreur lors de la création de l'assignation");
        return;
      }

      const worker = workers.find(w => w.id === selectedWorker);
      const worksite = worksites.find(w => w.id === selectedWorksite);
      
      console.log("✅ Assignation créée avec succès");
      toast.success(`Assignation créée avec succès pour ${worker?.name} sur ${worksite?.name}`);
      
      // Force a reload of the calendar page by navigating with state
      navigate("/calendar", { 
        state: { refresh: true, timestamp: Date.now() } 
      });
    } catch (error) {
      console.error('❌ Erreur lors de la création de l\'assignation:', error);
      toast.error("Erreur lors de la création de l'assignation");
    }
  };

  const handleCancel = () => {
    navigate("/calendar");
  };

  return {
    workers,
    worksites,
    selectedWorker,
    setSelectedWorker,
    selectedWorksite,
    setSelectedWorksite,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    loading,
    handleSubmit,
    handleCancel
  };
};
